import { useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { useSubscription } from '@apollo/client';
import { SelectOptionType } from '../../../../../../../components/dialog/helper';
import { useBackdropContext } from '../../../../../../../context/backdrop';
import { useInstallationContext } from '../../../../../../../context/installation';
import {
  ControlModeInternal,
  OnPartialConfigChangedSubscription,
  OnPartialConfigChangedSubscriptionVariables,
  SourceThresholdInternal,
} from '../../../../../../../data-access/gql-types/graphql';
import { ON_PARTIAL_CONFIG_CHANGED } from '../../../../../../../data-access/subscriptions/lavva-devices';
import { useRefetchData } from '../../../../../../../hooks/refresh-data/use-refetch-data';
import { useTimeout } from '../../../../../../../hooks/timeout/use-timeout';
import { durationParse, millisecondsToTime } from '../../../../../../../utils/helpers';
import { roundToOne } from '../../../../../../../utils/helpers/number';
import { toastSuccess } from '../../../../../../../utils/toast';
import { ChannelMeterType, ChannelType } from '../../../../../types/channel';
import { useMeterConfigurationContext } from '../context';
import { EventingRuleBody, ThresholdCondition } from '../types';
import { configurationInitialValues } from '../utils';

export const useMeterConfiguration = (channel: ChannelType) => {
  const { t } = useTranslation('channel-settings');
  const { t: tch } = useTranslation('channel-details');
  const {
    form: { setValue },
  } = useMeterConfigurationContext();
  const { skipLavvaFetch } = useInstallationContext();
  const { turnOffBackdrop } = useBackdropContext();
  const { refetchChannelDetails } = useRefetchData();
  const { setTimeoutError, clearTimeoutError } = useTimeout();
  const { data: configurationData } = useSubscription<
    OnPartialConfigChangedSubscription,
    OnPartialConfigChangedSubscriptionVariables
  >(ON_PARTIAL_CONFIG_CHANGED, {
    variables: { channelId: channel.id },
    skip: skipLavvaFetch,
  });
  const channelData = channel.data as ChannelMeterType;

  useEffect(() => {
    if (channelData.eventing) {
      let rules: EventingRuleBody[] = [];

      if (channelData.eventing.rules.length) {
        rules = channelData.eventing.rules.map((rule) => {
          let thresholdCondition: ThresholdCondition | null = null;
          let minValue: number | null = null;
          let maxValue: number | null = null;

          if (rule.thresholdEvent?.minValue) {
            thresholdCondition = ThresholdCondition.GreaterThan;
            minValue = rule.thresholdEvent.minValue;
          } else if (rule.thresholdEvent?.maxValue) {
            thresholdCondition = ThresholdCondition.LesserThan;
            maxValue = rule.thresholdEvent.maxValue;
          }

          return {
            enabled: rule.enabled,
            controlMode: rule.controlMode,

            source: rule.thresholdEvent?.source,
            deadTimeInMs: millisecondsToTime(rule.thresholdEvent?.deadTimeInMs || 0),
            hysteresis:
              rule.thresholdEvent?.hysteresis != null
                ? roundToOne(rule.thresholdEvent.hysteresis)
                : rule.thresholdEvent?.hysteresis != null
                ? rule.thresholdEvent.hysteresis
                : configurationInitialValues.hysteresis,
            thresholdCondition,
            minValue,
            maxValue,

            minPrice:
              rule.priceEvent?.minPrice != null
                ? roundToOne(rule.priceEvent.minPrice)
                : configurationInitialValues.minPrice,
            maxPrice:
              rule.priceEvent?.maxPrice != null
                ? roundToOne(rule.priceEvent.maxPrice)
                : configurationInitialValues.maxPrice,
            startAt: durationParse(rule.priceEvent?.startAt),
            endAt: durationParse(rule.priceEvent?.endAt),
            cheapestPeriods: rule.priceEvent?.cheapestPeriods?.toString() || configurationInitialValues.cheapestPeriods,
            minPriceChecked: rule.priceEvent?.minPrice != null,
            maxPriceChecked: rule.priceEvent?.maxPrice != null,
            cheapestPeriodsChecked: rule.priceEvent?.cheapestPeriods != null,

            isDefaultAction: rule.action.isDefaultAction,
            isNotifyAction: rule.action.isNotifyAction,
          } as EventingRuleBody;
        });
      } else {
        rules = channelData.eventing.supportedRelaysIndices.map(
          () =>
            ({
              minValue: configurationInitialValues.minValue,
              maxValue: configurationInitialValues.maxValue,
              hysteresis: configurationInitialValues.hysteresis,
              deadTimeInMs: millisecondsToTime(configurationInitialValues.deadTimeInMs),
              minPrice: configurationInitialValues.minPrice,
              maxPrice: configurationInitialValues.maxPrice,
              cheapestPeriods: configurationInitialValues.cheapestPeriods,
              startAt: configurationInitialValues.startAt,
              endAt: configurationInitialValues.endAt,
              minPriceChecked: configurationInitialValues.minPriceChecked,
              maxPriceChecked: configurationInitialValues.maxPriceChecked,
              cheapestPeriodsChecked: configurationInitialValues.cheapestPeriodsChecked,
            } as EventingRuleBody),
        );
      }

      setValue('rules', rules);
    }
  }, [channelData.eventing]);

  useEffect(() => {
    if (configurationData?.onPartialConfigChanged) {
      clearTimeoutError();
      const { updatedProperties } = configurationData.onPartialConfigChanged;

      const eventing = updatedProperties.find((x) => x === 'Eventing');
      if (eventing) {
        refetchChannelDetails();
        toastSuccess({ content: t('toast.editConfigurationSuccess') });
      }

      turnOffBackdrop();
    }
  }, [configurationData]);

  const controlModeOptions: SelectOptionType<ControlModeInternal>[] = (
    channelData.eventing?.supportedControlModes || []
  )
    .filter((mode) => mode !== ControlModeInternal.Unknown)
    .map((mode) => ({
      label: tch(`meterConfiguration.controlModes.${mode}`),
      value: mode,
    }));

  const sourceThresholdOptions: SelectOptionType<SourceThresholdInternal>[] = (
    channelData.eventing?.supportedSourceThresholds || []
  )
    .filter((source) => source !== SourceThresholdInternal.Unknown)
    .map((source) => ({
      label: tch(`energyMeasurements.types.phase.${source}`),
      value: source,
    }));

  return {
    controlModeOptions,
    sourceThresholdOptions,
    setTimeoutError,
  };
};
